import { Alert } from '@mui/material'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '../..'
import {
  advanceQuestionFlowAction,
  analyzeSuggestionsAction,
  AnswerTypes,
  DefectAssistantSuggestion,
  isQuestionUnanswered,
  OptionKinds,
  OptionValue,
  QnA,
  QuestionTypes,
} from '../../actions/defectAssistant'
import { createDefectCommentAction } from '../../actions/defectComments/actions'
import {
  appendCurrentComment,
  applyQuestionAnswer,
  clearCreateDefectAssistantErrors,
  excludeSuggestions,
  resetAnswersFromIndex,
  selectAssistantIsComplete,
  selectCreateDefectAssistantState,
  selectSuggestion,
} from '../../reducers/createDefectAssistantSlice'
import { clearCreateStatusByDefectId } from '../../reducers/defectComments'
import ConfirmationDialog from './ConfirmationDialog'
import { AiButton, EscapeLink } from './CtaButtons'
import { FinishStep, Interlude, SuggestionsBlock } from './scroll-ai-flow/flowSections'
import {
  ActiveSingleSelect,
  FutureSection,
  LockedDuplicateCheck,
  LockedQuestion,
  PlaceholderSection,
  ScrollDuplicateCheck,
  ScrollMultiSelect,
} from './scroll-ai-flow/questionSections'
import {
  Container,
  SCROLL_DELAY_MS,
  Section,
  SectionQuestion,
  SectionReasoning,
} from './scroll-ai-flow/styles'
import { CreateDefectFormData } from './types'

// Known step sequence — placeholders shown even before the backend returns them.
const KNOWN_STEPS = [
  { type: QuestionTypes.DEFECT_TYPE, labelKey: 'defect.field.defectType' },
  { type: QuestionTypes.PHYSICAL_ITEM, labelKey: 'defect.aiAssistant.questionType.physicalItem' },
  { type: QuestionTypes.COMPLETENESS, labelKey: 'defect.aiAssistant.questionType.completeness' },
] as const

interface ScrollAiFlowProps {
  applyAiSuggestion: (defectCode: string, defectType: string) => void
  onEnterManualNoAi: () => void
  onEnterManualAfterAi: () => void
}

const ScrollAiFlow = ({
  applyAiSuggestion,
  onEnterManualNoAi,
  onEnterManualAfterAi,
}: ScrollAiFlowProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()
  const { setValue } = useFormContext<CreateDefectFormData>()

  const state = useTypedSelector(selectCreateDefectAssistantState)
  const {
    phase,
    additionalDetailsVisible,
    analyzeStatus,
    questionStatus,
    suggestions,
    metaOptions,
    defectCodeAdviceByGeneration,
    generationId,
    body,
    questionAnswers,
  } = state
  const isComplete = useTypedSelector(selectAssistantIsComplete)
  const items = useTypedSelector((s) => s.defect.items.data)

  const [additionalDetails, setAdditionalDetails] = useState('')
  // Each discarded suggestion round becomes permanent locked history: the suggestions that were
  // shown plus the extra detail the user typed before requesting new ones. New suggestions append
  // below, so the page stays static and the user can see what came before and after each interlude.
  const [discardedRounds, setDiscardedRounds] = useState<
    { suggestions: DefectAssistantSuggestion[]; detail: string; generationId: string | null }[]
  >([])
  const [amendDefectId, setAmendDefectId] = useState<string | null>(null)
  const [showAbortConfirm, setShowAbortConfirm] = useState(false)
  const [reEditingIdx, setReEditingIdx] = useState<number | null>(null)
  const amendStatus = useTypedSelector((s) =>
    amendDefectId ? s.defectComments.createStatusByDefectId[amendDefectId] : undefined
  )

  const activeRef = useRef<HTMLDivElement>(null)
  const lastInterludeRef = useRef<HTMLDivElement>(null)
  const questionFlowThunkRef = useRef<{ abort: () => void } | null>(null)
  const pendingDuplicateCommentRef = useRef(false)

  const dispatchAdvanceQuestionFlow = () => {
    questionFlowThunkRef.current?.abort()
    questionFlowThunkRef.current = dispatch(advanceQuestionFlowAction())
  }

  const firstUnansweredIdx = questionAnswers.findIndex(isQuestionUnanswered)
  const currentQuestion = firstUnansweredIdx >= 0 ? questionAnswers[firstUnansweredIdx] : undefined
  // Index of the most recently answered question — used to keep a just-submitted multiselect active
  // (with its spinner) while the next step loads, instead of flipping straight to the locked view.
  const lastAnsweredIdx = questionAnswers.reduce(
    (acc, q, idx) => (isQuestionUnanswered(q) ? acc : idx),
    -1
  )

  // While re-editing a multiselect the answers aren't reset until submit, so isComplete/aiComment
  // are still set — but the flow is no longer "done": hide the Kuvaus summary and finish button.
  const flowComplete = isComplete && questionStatus !== 'loading' && reEditingIdx === null
  const questionLoading = questionStatus === 'loading'

  // If the questions list shrinks (abort/error/new round) while a re-edit is pending, the stale
  // index would pin flowComplete=false and hide the finish button. Drop it when it no longer points
  // at a re-editable multiselect.
  useEffect(() => {
    if (reEditingIdx === null) return
    const q = questionAnswers[reEditingIdx]
    if (!q || q.answerType !== AnswerTypes.MULTISELECT) setReEditingIdx(null)
  }, [questionAnswers, reEditingIdx])

  // Scroll to the newly active section whenever the active index changes
  useEffect(() => {
    if (!activeRef.current) return
    const timer = setTimeout(() => {
      activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, SCROLL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [firstUnansweredIdx, flowComplete])

  // After a retry returns fresh suggestions, scroll to the "Kerro lisää viasta" interlude so the
  // user sees the context (what they added) with the extra options continuing below — not jumped
  // to a second list as if it were unrelated.
  useEffect(() => {
    if (phase !== 'suggestions' || analyzeStatus !== 'succeeded' || discardedRounds.length === 0)
      return
    const timer = setTimeout(() => {
      lastInterludeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, SCROLL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [analyzeStatus, phase, discardedRounds.length])

  const handlePickSuggestion = (suggestion: DefectAssistantSuggestion) => {
    applyAiSuggestion(suggestion.defectCode, '')
    dispatch(selectSuggestion(suggestion))
    dispatchAdvanceQuestionFlow()
  }

  const handleMetaOption = (option: OptionValue) => {
    if (option.kind === OptionKinds.GIVE_UP) {
      setValue('comment', body.userComment)
      onEnterManualNoAi()
      return
    }
    dispatch(excludeSuggestions(suggestions.map((s) => s.defectCode)))
  }

  const handleSubmitAdditionalDetails = () => {
    const trimmed = additionalDetails.trim()
    // Commit the current round to history (its suggestions + the detail just entered + the
    // generationId whose defectCodeAdvice is still streaming) so it stays visible and locked between the
    // previous and the upcoming suggestions. We store the generationId rather than a snapshot of the
    // defectCodeAdvice text: the locked block renders defectCodeAdviceByGeneration[generationId] live, so defectCodeAdvice that
    // hadn't finished when the user moved on keeps filling in (spinner → text) — the in-flight GETs
    // aren't aborted and defectCodeAdviceByGeneration is no longer wiped per round.
    setDiscardedRounds((prev) => [...prev, { suggestions, detail: trimmed, generationId }])
    if (trimmed.length > 0) {
      // appendCurrentComment mirrors this join in the reducer; compute it once here to keep the RHF
      // 'comment' field in sync without recomputing the concatenation in two spots.
      const nextComment =
        body.userComment.length > 0 ? `${body.userComment}\n\n${trimmed}` : trimmed
      dispatch(appendCurrentComment(trimmed))
      setValue('comment', nextComment)
    }
    setAdditionalDetails('')
    dispatch(analyzeSuggestionsAction())
  }

  const submitAnswers = (answers: OptionValue[]) => {
    if (questionLoading) return
    dispatch(applyQuestionAnswer(answers))
    dispatchAdvanceQuestionFlow()
  }

  const handleFinishAndSend = () => {
    const duplicateAnswer = state.questionAnswers.find(
      (qna) => qna.questionType === QuestionTypes.DUPLICATE_CHECK
    )?.answers?.[0]
    if (duplicateAnswer?.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT) {
      const defectId = duplicateAnswer.value != null ? String(duplicateAnswer.value) : ''
      const comment = duplicateAnswer.additionalInfo ?? ''
      if (defectId) {
        setAmendDefectId(defectId)
        dispatch(createDefectCommentAction({ defectId, comment }))
        return
      }
    }

    if (body.aiComment) setValue('aiComment', body.aiComment)
    setValue('comment', body.userComment)

    const resolvedItemId = body.itemId
    const matchedUniqueId =
      resolvedItemId != null
        ? Object.keys(items).find((key) => items[key].itemId === resolvedItemId)
        : undefined
    if (matchedUniqueId) {
      setValue('type', 'item')
      setValue('installationItem', matchedUniqueId)
      setValue('installationSerialNumber', items[matchedUniqueId].serialNumber)
    }

    // Hand the AI-resolved code/type to applyAiSuggestion (in CreateDefectPage). It resolves the tree
    // row — body.defectCode is a product-group prefix, not a full code — and, if the resolved item's
    // defect tree isn't loaded yet, fetches it and re-applies once available so Vikatyyppi fills in.
    applyAiSuggestion(body.defectCode, body.defectType)

    onEnterManualAfterAi()
  }

  // ------------------------------------------------------------------
  // Section dispatcher — picks the right component for a question's state
  // ------------------------------------------------------------------

  const suggestionPicked = phase === 'questions'
  const selectedCode = body.defectCode
  const analyzeLoading = analyzeStatus === 'loading'

  const renderQuestionSection = (
    qna: QnA | undefined,
    stepType: string,
    stepLabel: string,
    qnaIdxInAll: number // index in questionAnswers (-1 if not yet returned)
  ): JSX.Element | null => {
    // While re-editing a multiselect, hide everything that comes after it
    if (reEditingIdx !== null && qnaIdxInAll > reEditingIdx) return null

    // Show the multiselect being re-edited as active (answers still in Redux, not yet reset)
    if (reEditingIdx !== null && qnaIdxInAll === reEditingIdx && qna) {
      return (
        <div key={qna.uuid} ref={activeRef}>
          <ScrollMultiSelect
            question={qna}
            loading={questionLoading}
            onSubmit={(answers) => {
              dispatch(resetAnswersFromIndex(reEditingIdx))
              dispatch(applyQuestionAnswer(answers))
              dispatchAdvanceQuestionFlow()
              setReEditingIdx(null)
            }}
          />
        </div>
      )
    }

    // Pure placeholder (not yet returned by backend)
    if (!qna) {
      const isFirstLoading = questionLoading && qnaIdxInAll === -1 && !currentQuestion
      return (
        <PlaceholderSection key={stepType} stepLabel={stepLabel} firstLoading={isFirstLoading} />
      )
    }

    const answered = !isQuestionUnanswered(qna)
    const isActive = currentQuestion?.uuid === qna.uuid

    // Just-submitted multiselect while the next step loads: keep it active with its spinner "Jatka…"
    // button (same instance, so the ticked boxes persist) until the new bits arrive.
    if (
      qna.answerType === AnswerTypes.MULTISELECT &&
      answered &&
      questionLoading &&
      !currentQuestion &&
      qnaIdxInAll === lastAnsweredIdx
    ) {
      return (
        <div key={qna.uuid} ref={activeRef}>
          <ScrollMultiSelect question={qna} loading onSubmit={submitAnswers} />
        </div>
      )
    }

    // Future question (returned but not yet reachable)
    if (!answered && !isActive) {
      return <FutureSection key={qna.uuid} qna={qna} stepLabel={stepLabel} />
    }

    // Answered (locked) — clicking re-opens it
    if (answered) {
      if (qna.questionType === QuestionTypes.DUPLICATE_CHECK) {
        return (
          <LockedDuplicateCheck
            key={qna.uuid}
            qna={qna}
            onReopen={(opt) => {
              if (opt.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT) {
                pendingDuplicateCommentRef.current = true
              }
              dispatch(resetAnswersFromIndex(qnaIdxInAll))
            }}
            onAbort={() => setShowAbortConfirm(true)}
          />
        )
      }
      const isMulti = qna.answerType === AnswerTypes.MULTISELECT
      return (
        <LockedQuestion
          key={qna.uuid}
          qna={qna}
          onReopen={() =>
            isMulti ? setReEditingIdx(qnaIdxInAll) : dispatch(resetAnswersFromIndex(qnaIdxInAll))
          }
        />
      )
    }

    // Active question — full interaction
    if (qna.questionType === QuestionTypes.DUPLICATE_CHECK) {
      return (
        <div key={qna.uuid} ref={activeRef}>
          <ScrollDuplicateCheck
            question={qna}
            loading={questionLoading}
            amendLoading={amendStatus === 'loading'}
            pendingCommentOpen={pendingDuplicateCommentRef}
            onSubmit={submitAnswers}
            onAmendComment={(defectId, comment) => {
              setAmendDefectId(defectId)
              dispatch(createDefectCommentAction({ defectId, comment }))
            }}
            onAbortRequest={() => setShowAbortConfirm(true)}
          />
        </div>
      )
    }
    if (qna.answerType === AnswerTypes.MULTISELECT) {
      return (
        <div key={qna.uuid} ref={activeRef}>
          <ScrollMultiSelect question={qna} loading={questionLoading} onSubmit={submitAnswers} />
        </div>
      )
    }
    return (
      <div key={qna.uuid} ref={activeRef}>
        <ActiveSingleSelect
          qna={qna}
          loading={questionLoading}
          onPick={(opt) => !questionLoading && submitAnswers([opt])}
        />
      </div>
    )
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------

  if (phase === 'failed') {
    return (
      <Container>
        <Alert severity="error">
          {analyzeStatus === 'failed'
            ? t('defect.aiAssistant.error.suggestions')
            : t('defect.aiAssistant.error.questions')}
        </Alert>
        <AiButton
          type="button"
          onClick={() => {
            dispatch(clearCreateDefectAssistantErrors())
            dispatch(analyzeSuggestionsAction())
          }}
        >
          {t('defect.aiAssistant.retry')}
        </AiButton>
      </Container>
    )
  }

  // ------------------------------------------------------------------
  // Main render — suggestions, then known steps, then finish
  // ------------------------------------------------------------------

  const knownStepTypes = KNOWN_STEPS.map((s) => s.type as string)

  // Any questions not in KNOWN_STEPS (e.g. DUPLICATE_CHECK) rendered after the known sequence
  const extraQuestions = questionAnswers.filter((q) => !knownStepTypes.includes(q.questionType))

  // First analysis came back with nothing to choose from — make that explicit instead of leaving the
  // user on a screen of dimmed placeholders with no obvious next step.
  const showNoSuggestions =
    phase === 'suggestions' &&
    analyzeStatus === 'succeeded' &&
    suggestions.length === 0 &&
    discardedRounds.length === 0 &&
    !additionalDetailsVisible

  return (
    <Container>
      {/* Past rounds: suggestions stay choosable, interlude stays in place. The most recent interlude
          shows the loading spinner while the next round is fetched (unless the user already picked),
          so nothing appears or disappears. */}
      {discardedRounds.map((round, i) => {
        const isLast = i === discardedRounds.length - 1
        const loading = isLast && analyzeLoading && !suggestionPicked
        return (
          <Fragment key={`round-${i}`}>
            <SuggestionsBlock
              sugs={round.suggestions}
              activeBlock={false}
              showHeader={i === 0}
              suggestionPicked={suggestionPicked}
              selectedCode={selectedCode}
              metaOptions={metaOptions}
              additionalDetailsVisible={additionalDetailsVisible}
              analyzeLoading={analyzeLoading}
              // This round's own defectCodeAdvice map — keeps filling in even though it's locked history now.
              defectCodeAdviceByCode={
                round.generationId ? defectCodeAdviceByGeneration[round.generationId] : undefined
              }
              onPick={handlePickSuggestion}
              onMeta={handleMetaOption}
              onExclude={(codes) => dispatch(excludeSuggestions(codes))}
            />
            {isLast ? (
              <div ref={lastInterludeRef}>
                <Interlude
                  mode={loading ? 'loading' : 'locked'}
                  detail={round.detail}
                  value={additionalDetails}
                  onChange={setAdditionalDetails}
                  onSubmit={handleSubmitAdditionalDetails}
                />
              </div>
            ) : (
              <Interlude
                mode="locked"
                detail={round.detail}
                value={additionalDetails}
                onChange={setAdditionalDetails}
                onSubmit={handleSubmitAdditionalDetails}
              />
            )}
          </Fragment>
        )
      })}

      {/* Current (active) suggestions — shown whenever a current round exists (hidden only during
          retry loading when redux has cleared them; the spinner then lives in the interlude above). */}
      {suggestions.length > 0 && (
        <SuggestionsBlock
          sugs={suggestions}
          activeBlock
          showHeader={discardedRounds.length === 0}
          suggestionPicked={suggestionPicked}
          selectedCode={selectedCode}
          metaOptions={metaOptions}
          additionalDetailsVisible={additionalDetailsVisible}
          analyzeLoading={analyzeLoading}
          defectCodeAdviceByCode={
            generationId ? defectCodeAdviceByGeneration[generationId] : undefined
          }
          onPick={handlePickSuggestion}
          onMeta={handleMetaOption}
          onExclude={(codes) => dispatch(excludeSuggestions(codes))}
        />
      )}

      {showNoSuggestions && (
        <Section>
          <SectionReasoning>{t('defect.aiAssistant.noSuggestions')}</SectionReasoning>
        </Section>
      )}

      {/* Active "Kerro lisää viasta" input for the current round */}
      {additionalDetailsVisible && phase === 'suggestions' && (
        <Interlude
          mode="input"
          detail=""
          value={additionalDetails}
          onChange={setAdditionalDetails}
          onSubmit={handleSubmitAdditionalDetails}
        />
      )}

      {KNOWN_STEPS.map(({ type, labelKey }) => {
        const idx = questionAnswers.findIndex((q) => q.questionType === type)
        return renderQuestionSection(
          idx >= 0 ? questionAnswers[idx] : undefined,
          type,
          labelKey,
          idx
        )
      })}

      {extraQuestions.map((q) => {
        const idx = questionAnswers.indexOf(q)
        return renderQuestionSection(q, q.questionType, q.question, idx)
      })}

      {body.aiComment && flowComplete && (
        <Section>
          <SectionQuestion>{t('defect.aiAssistant.aiCommentTitle')}</SectionQuestion>
          <SectionReasoning>{body.aiComment}</SectionReasoning>
        </Section>
      )}

      <FinishStep
        flowComplete={flowComplete}
        amendLoading={amendStatus === 'loading'}
        activeRef={activeRef}
        onFinish={handleFinishAndSend}
      />

      <SectionReasoning style={{ textAlign: 'center', padding: '8px 4px' }}>
        {t('defect.aiAssistant.editSelectionsHint')}
      </SectionReasoning>

      {!flowComplete && (
        <EscapeLink type="button" onClick={onEnterManualNoAi}>
          {t('defect.aiAssistant.continueToForm')}
        </EscapeLink>
      )}

      {amendStatus === 'failed' && (
        <Alert severity="error">{t('defect.comment.create.error')}</Alert>
      )}
      {amendStatus === 'succeeded' && amendDefectId && (
        <ConfirmationDialog
          dialogText={t('defect.comment.create.success')}
          primaryActionText={t('back')}
          closeDialog={() => {
            dispatch(clearCreateStatusByDefectId(amendDefectId))
            setAmendDefectId(null)
          }}
          submitHandler={() => {
            dispatch(clearCreateStatusByDefectId(amendDefectId))
            setAmendDefectId(null)
            window.history.back()
          }}
        />
      )}
      {showAbortConfirm && (
        <ConfirmationDialog
          dialogText={t('defect.aiAssistant.duplicate.abortConfirmText')}
          primaryActionText={t('defect.aiAssistant.duplicate.abortConfirmButton')}
          secondaryActionText={t('defect.aiAssistant.duplicate.abortCancelButton')}
          closeDialog={() => setShowAbortConfirm(false)}
          submitHandler={() => window.history.back()}
        />
      )}
    </Container>
  )
}

export default ScrollAiFlow
